import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { createPortal } from "react-dom";
import { DayPicker } from "react-day-picker";
import { it } from "date-fns/locale";
import { differenceInCalendarDays } from "date-fns";
import { CalendarDays, ChevronDown, X } from "lucide-react";
import "react-day-picker/dist/style.css";
import "./OrderPeriodPicker.css";

const ymd = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const parse = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return ymd(date) === value ? date : undefined;
};
const labelDate = value => parse(value)?.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) || "";
const describe = (from, to) => from && to ? `${labelDate(from)} – ${labelDate(to)}` : from ? `Dal ${labelDate(from)}` : to ? `Fino al ${labelDate(to)}` : "Tutti i periodi";
const compactDate = value => parse(value)?.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" }) || "";
const compactPeriod = (from, to) => from && to ? `${compactDate(from)} – ${compactDate(to)}` : from ? `Dal ${compactDate(from)}` : to ? `Fino al ${compactDate(to)}` : "Tutti i periodi";
const presets = [["today", "Oggi"], ["last7", "7 giorni"], ["last30", "30 giorni"], ["month", "Questo mese"], ["previous", "Mese scorso"]];
export function orderPeriodPreset(key, today = new Date()) {
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const start = new Date(end);
  if (key === "last7") start.setDate(start.getDate() - 6);
  if (key === "last30") start.setDate(start.getDate() - 29);
  if (key === "month") start.setDate(1);
  if (key === "previous") return { from: ymd(new Date(end.getFullYear(), end.getMonth() - 1, 1)), to: ymd(new Date(end.getFullYear(), end.getMonth(), 0)) };
  return { from: ymd(start), to: ymd(end) };
}

export default function OrderPeriodPicker({ from, to, onApply }) {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 760px)").matches);
  const [draft, setDraft] = useState({ from: "", to: "" });
  const [mode, setMode] = useState("range");
  const [hover, setHover] = useState("");
  const [month, setMonth] = useState(new Date());
  const [position, setPosition] = useState({ visibility: "hidden" });
  const trigger = useRef(null), popup = useRef(null);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 760px)");
    const update = event => setMobile(event.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  function show() {
    setDraft({ from: parse(from) ? from : "", to: parse(to) ? to : "" });
    setMode(from && !to ? "from" : to && !from ? "to" : "range");
    setMonth(parse(from) || parse(to) || new Date());
    setHover(""); setOpen(true);
  }
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    if (mobile) document.body.style.overflow = "hidden";
    popup.current?.focus({ preventScroll: true });
    const outside = event => {
      if (!mobile && !popup.current?.contains(event.target) && !trigger.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => {
      if (mobile) document.body.style.overflow = previous;
      document.removeEventListener("pointerdown", outside);
      trigger.current?.focus({ preventScroll: true });
    };
  }, [open, mobile]);
  useEffect(() => {
    if (!open || !Capacitor.isNativePlatform()) return;
    const listener = CapacitorApp.addListener("backButton", () => setOpen(false));
    return () => { listener.then(handle => handle.remove()).catch(() => {}); };
  }, [open]);
  useLayoutEffect(() => {
    if (!open || mobile) return;
    const place = () => {
      if (!popup.current || !trigger.current) return;
      const rect = trigger.current.getBoundingClientRect();
      const viewport = window.visualViewport;
      const vx = viewport?.offsetLeft || 0, vy = viewport?.offsetTop || 0;
      const width = Math.min(660, (viewport?.width || window.innerWidth) - 24);
      const bottom = vy + (viewport?.height || window.innerHeight) - 12;
      const maxHeight = bottom - vy - 12;
      const height = Math.min(popup.current.scrollHeight, maxHeight);
      const top = rect.bottom + 8 + height <= bottom ? rect.bottom + 8 : rect.top - height - 8 >= vy + 12 ? rect.top - height - 8 : Math.max(vy + 12, bottom - height);
      const left = Math.max(vx + 12, Math.min(rect.left, vx + (viewport?.width || window.innerWidth) - width - 12));
      setPosition({ width, maxHeight, top, left, visibility: "visible" });
    };
    place(); window.addEventListener("resize", place); window.addEventListener("scroll", place, true);
    window.visualViewport?.addEventListener("resize", place); window.visualViewport?.addEventListener("scroll", place);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(place) : null;
    observer?.observe(popup.current);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); window.visualViewport?.removeEventListener("resize", place); window.visualViewport?.removeEventListener("scroll", place); observer?.disconnect(); };
  }, [open, mobile]);
  function pick(date) {
    const value = ymd(date); setHover("");
    if (mode === "from") setDraft({ from: value, to: "" });
    else if (mode === "to") setDraft({ from: "", to: value });
    else if (!draft.from || draft.to) setDraft({ from: value, to: "" });
    else setDraft({ from: value < draft.from ? value : draft.from, to: value < draft.from ? draft.from : value });
  }
  function single(nextMode) {
    setMode(nextMode); setHover("");
    setDraft(nextMode === "from" ? { from: draft.from || draft.to, to: "" } : { from: "", to: draft.to || draft.from });
  }
  function keyboard(event) {
    if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); setOpen(false); }
    if (event.key === "Tab") {
      const nodes = [...popup.current.querySelectorAll('button:not(:disabled), [tabindex="0"]')];
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === popup.current)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  }
  const previewEnd = mode === "range" && draft.from && !draft.to && hover ? hover : draft.to;
  const low = draft.from && previewEnd ? (draft.from < previewEnd ? draft.from : previewEnd) : draft.from;
  const high = draft.from && previewEnd ? (draft.from > previewEnd ? draft.from : previewEnd) : draft.to;
  const valid = mode === "range" ? !!(parse(draft.from) && parse(draft.to) && draft.from <= draft.to) : !!parse(mode === "from" ? draft.from : draft.to);
  const activePreset = presets.find(([key]) => { const range = orderPeriodPreset(key); return range.from === draft.from && range.to === draft.to; })?.[0];
  return <>
    <button ref={trigger} type="button" className="orp-trigger" aria-label={`Periodo ordini: ${describe(from, to)}`} title={describe(from, to)} aria-haspopup="dialog" aria-expanded={open} onClick={() => open ? setOpen(false) : show()}>
      <CalendarDays size={20} aria-hidden="true" /><span className="orp-trigger-label-long">{describe(from, to)}</span><span className="orp-trigger-label-short" aria-hidden="true">{compactPeriod(from, to)}</span><ChevronDown size={18} aria-hidden="true" />
    </button>
    {open && createPortal(<div className={`orp-overlay ${mobile ? "orp-mobile" : "orp-desktop"}`} onPointerDown={event => { if (event.target === event.currentTarget) setOpen(false); }}>
      <div ref={popup} className="orp-popup" style={mobile ? undefined : position} role="dialog" aria-modal="true" aria-label="Seleziona periodo" tabIndex={-1} onKeyDown={keyboard}>
        <header className="orp-header"><h2>Seleziona periodo</h2><button type="button" aria-label="Chiudi calendario" onClick={() => setOpen(false)}><X size={20} /></button></header>
        <div className="orp-presets">
          {presets.map(([key, label]) => <button type="button" key={key} aria-pressed={activePreset === key} onClick={() => { onApply(orderPeriodPreset(key)); setOpen(false); }}>{label}</button>)}
          <button type="button" aria-pressed={!activePreset && mode === "range"} onClick={() => { setMode("range"); setDraft({ from: "", to: "" }); setHover(""); }}>Personalizzato</button>
        </div>
        <DayPicker locale={it} month={month} onMonthChange={setMonth} numberOfMonths={mobile ? 1 : 2} weekStartsOn={1} showOutsideDays
          labels={{ labelNext: () => "Mese successivo", labelPrevious: () => "Mese precedente" }}
          formatters={{ formatWeekdayName: date => date.toLocaleDateString("it-IT", { weekday: "short" }) }}
          onDayClick={pick} onDayMouseEnter={date => { if (!mobile && mode === "range" && draft.from && !draft.to) setHover(ymd(date)); }} onDayMouseLeave={() => setHover("")}
          modifiers={{ rangeStart: parse(low), rangeEnd: parse(high), rangeMiddle: low && high ? { after: parse(low), before: parse(high) } : [] }}
          modifiersClassNames={{ rangeStart: "orp-range-start", rangeEnd: "orp-range-end", rangeMiddle: "orp-range-middle" }} />
        <footer className="orp-footer">
          <div className="orp-selected"><span>Periodo selezionato</span><strong>{describe(draft.from, draft.to)}</strong>{draft.from && draft.to && <small>({differenceInCalendarDays(parse(draft.to), parse(draft.from)) + 1} giorni)</small>}</div>
          <div className="orp-open-range"><button type="button" aria-pressed={mode === "from"} onClick={() => single("from")}><CalendarDays size={16} />Solo dal</button><button type="button" aria-pressed={mode === "to"} onClick={() => single("to")}><CalendarDays size={16} />Solo al</button></div>
          <div className="orp-actions"><button type="button" onClick={() => setOpen(false)}>Annulla</button><button type="button" disabled={!valid} onClick={() => { if (valid) { onApply(draft); setOpen(false); } }}>Conferma</button></div>
        </footer>
      </div>
    </div>, document.body)}
  </>;
}
